import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeConselho } from '@/services/cade';
import type { ConselhoBench, ConselhoMethod } from '@/services/types';

/**
 * The plain-language methodology for the CADE Tribunal bench.
 *
 * Didactic copy (i18n) over live lines read from the SAME endpoint and query
 * key as /cade/conselho, so every floor, year and count quoted here is the one
 * the bench applies. The copy carries no numbers of its own; when the bench
 * refuses a panel, its live line shows the same refusal and the prose stays.
 */
export function CadeConselhoMethodologyPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'conselho'],
    queryFn: fetchCadeConselho,
  });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });

  const k = (key: string) => `cade:conselhoMethodology.sections.${key}`;
  const p = (key: string) => <p className="text-sm text-muted-foreground">{t(k(key))}</p>;
  // A live line waits on the bench; the prose around it never does.
  const live = (name: string, render: (bench: ConselhoBench) => ReactNode) => {
    if (isPending) return <SkeletonLoader rows={1} />;
    if (error || !data) return null;
    return <div data-live={name}>{render(data)}</div>;
  };
  // A floor is quoted only from the payload; without the method block, nothing.
  const withMethod = (name: string, render: (method: ConselhoMethod) => ReactNode) =>
    live(name, (bench) => (bench.method ? render(bench.method) : null));
  const line = (text: string) => <p className="text-sm text-foreground">{text}</p>;

  const section = (key: string, children: ReactNode) => (
    <section className="space-y-2 rounded-lg border bg-card p-4" data-section={key}>
      <h2 className="text-sm font-semibold text-foreground">{t(k(`${key}.title`))}</h2>
      {children}
    </section>
  );

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:conselhoMethodology.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:conselhoMethodology.subtitle')}</p>
      </header>

      {error ? <ErrorMessage message={error.message} onRetry={() => void refetch()} /> : null}
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {section('sources', <>{p('sources.p1')}{p('sources.p2')}</>)}
      {section('minutes', <>{p('minutes.p1')}{p('minutes.p2')}</>)}
      {section(
        'complete',
        <>
          {p('complete.p1')}
          {p('complete.p2')}
          {withMethod('complete', (m) => {
            const years = m.complete_years ?? [];
            return line(
              years.length
                ? t(k('complete.live'), { years: years.join(', ') })
                : t(k('complete.none')),
            );
          })}
        </>,
      )}
      {section(
        'follow',
        <>
          {p('follow.p1')}
          {p('follow.p2')}
          {p('follow.p3')}
          {live('follow', (bench) => (
            <Answer answer={bench.follow}>
              {(rows) =>
                bench.method ? line(t(k('follow.live'), { members: rows.length, floor: bench.method.floors.metric })) : null
              }
            </Answer>
          ))}
        </>,
      )}
      {section(
        'dissent',
        <>
          {p('dissent.p1')}
          {p('dissent.p2')}
          {live('dissent', (bench) =>
            bench.outvoted ? (
              <Answer answer={bench.outvoted}>
                {(rows) =>
                  bench.method
                    ? line(t(k('dissent.live'), { members: rows.length, floor: bench.method.floors.metric }))
                    : null
                }
              </Answer>
            ) : null,
          )}
        </>,
      )}
      {section(
        'outvoted',
        <>
          {p('outvoted.p1')}
          {p('outvoted.p2')}
          {p('outvoted.p3')}
          {live('outvoted', (bench) =>
            bench.relator_outvoted ? (
              <Answer answer={bench.relator_outvoted}>
                {(rows) =>
                  bench.method
                    ? line(t(k('outvoted.live'), { relators: rows.length, floor: bench.method.floors.relator }))
                    : null
                }
              </Answer>
            ) : null,
          )}
        </>,
      )}
      {section(
        'positions',
        <>
          {p('positions.p1')}
          {p('positions.p2')}
          {live('positions', (bench) => (
            <Answer answer={bench.positions}>
              {(rows) =>
                bench.method
                  ? line(t(k('positions.live'), { members: rows.length, floor: bench.method.floors.position }))
                  : null
              }
            </Answer>
          ))}
        </>,
      )}
      {section(
        'other',
        <>
          {p('other.p1')}
          {withMethod('pairs', (m) =>
            line(t(k('other.p2'), { pair: m.floors.pair, contested: m.floors.pair_contested })),
          )}
          {p('other.p3')}
          {withMethod('docket', (m) => line(t(k('other.p4'), { docket: m.floors.docket })))}
        </>,
      )}
      {section(
        'ordering',
        <>
          {p('ordering.p1')}
          {withMethod('intervals', (m) =>
            line(t('common:conselhoMethodology.intervals', { level: percent.format(m.interval_level) })),
          )}
        </>,
      )}

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="never">
        <h2 className="text-sm font-semibold text-foreground">{t('common:conselhoMethodology.neverClaims.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p1')}</p>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p2')}</p>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p3')}</p>
      </section>

      <nav className="flex flex-wrap gap-4 text-xs">
        <Link to="/cade/conselho" className="text-primary underline-offset-2 hover:underline">
          {t('cade:conselhoMethodology.links.bench')}
        </Link>
        <Link to="/cade/conselho/items" className="text-primary underline-offset-2 hover:underline">
          {t('cade:conselhoMethodology.links.items')}
        </Link>
      </nav>
    </div>
  );
}
